import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';

import { apiRequest } from 'src/helpers/api';
import {
  CreateExtractionDto,
  Extraction,
  UpdateExtractionDto,
} from 'src/types/extraction.type';
import { Procedure } from 'src/types/procedure.type';
import { Victime } from 'src/types/victime.type';
import { pendingReducer, rejectedReducer } from '../helpers/slice';

/* Thunks */

export const fetchAllExtractions = createAsyncThunk(
  'extractions/fetchAll',
  async (organizationId?: string) => {
    if (organizationId) {
      return await apiRequest<Extraction[]>(
        'GET',
        `/organization/${organizationId}/extractions`,
      );
    } else {
      return await apiRequest<Extraction[]>('GET', '/extraction');
    }
  },
);

export const fetchExtractionByVictimeId = createAsyncThunk(
  'extraction/fetchByVictimeId',

  async ({ organizationId, victimeId }: { organizationId: string; victimeId: string }) => {
    return await apiRequest<Extraction>('GET', `/organization/${organizationId}/extraction/by-victime/${victimeId}`);
  },
);

export const fetchExtractionById = createAsyncThunk(
  'extraction/fetchById',
  async (extractionId: string) => {
    return await apiRequest<Extraction>('GET', `/extraction/${extractionId}`);
  },
);

export const deleteExtractionById = createAsyncThunk(
  'extraction/deleteById',
  async (extractionId: string) => {
    return await apiRequest<void>('DELETE', `/extraction/${extractionId}`);
  },
);

export const createExtraction = createAsyncThunk(
  'extraction/create',
  async (params: { dto: CreateExtractionDto; organizationId: string }) => {
    const { dto, organizationId } = params;
    const formData = new FormData();
    formData.append('file', dto.file);
    return await apiRequest<{
      extraction: Extraction;
      procedure: Procedure;
      victime: Victime;
    }>(
      'POST',
      `/organization/${organizationId}/extraction`,
      undefined,
      formData,
      300000,
    );
  },
);

export const createExtractionVictime = createAsyncThunk(
  'extraction/createVictime',
  async (params: { dto: CreateExtractionDto; organizationId: string }) => {
    const { dto, organizationId } = params;
    return await apiRequest<Extraction>(
      'POST',
      `/organization/${organizationId}/extraction`,
      undefined,
      dto,
    );
  },
);

export const updateExtraction = createAsyncThunk(
  'extraction/update',
  async ({ id, data }: { id: string; data: UpdateExtractionDto }) => {
    return await apiRequest<Extraction>(
      'PATCH',
      `/extractions/${id}`,
      undefined,
      data,
    );
  },
);

/* Slice */

export const extractionAdapter = createEntityAdapter<Extraction>({
  selectId: (extraction: Extraction) => extraction._id,
});

const extractionSlice = createSlice({
  name: 'extraction',
  initialState: extractionAdapter.getInitialState({
    isLoading: false,
  }),
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllExtractions.pending, pendingReducer)
      .addCase(fetchAllExtractions.rejected, rejectedReducer)
      .addCase(fetchAllExtractions.fulfilled, (state, { payload }) => {
        extractionAdapter.setAll(state, payload);
        state.isLoading = false;
      })
      .addCase(fetchExtractionById.pending, pendingReducer)
      .addCase(fetchExtractionById.rejected, rejectedReducer)
      .addCase(fetchExtractionById.fulfilled, (state, { payload }) => {
        extractionAdapter.setOne(state, payload);
        state.isLoading = false;
      })
      .addCase(fetchExtractionByVictimeId.pending, pendingReducer)
      .addCase(fetchExtractionByVictimeId.rejected, rejectedReducer)
      .addCase(fetchExtractionByVictimeId.fulfilled, (state, { payload }) => {
        extractionAdapter.setOne(state, payload);
        state.isLoading = false;
      })
      .addCase(createExtraction.pending, pendingReducer)
      .addCase(createExtraction.rejected, rejectedReducer)
      .addCase(createExtraction.fulfilled, (state, { payload }) => {
        extractionAdapter.addOne(state, payload.extraction);
        state.isLoading = false;
      })
      .addCase(createExtractionVictime.pending, pendingReducer)
      .addCase(createExtractionVictime.rejected, rejectedReducer)
      .addCase(createExtractionVictime.fulfilled, (state, { payload }) => {
        extractionAdapter.addOne(state, payload);
        state.isLoading = false;
      })
      .addCase(updateExtraction.pending, pendingReducer)
      .addCase(updateExtraction.rejected, rejectedReducer)
      .addCase(updateExtraction.fulfilled, (state, { payload }) => {
        extractionAdapter.setOne(state, payload);
        state.isLoading = false;
      })
      .addCase(deleteExtractionById.pending, pendingReducer)
      .addCase(deleteExtractionById.rejected, rejectedReducer)
      .addCase(deleteExtractionById.fulfilled, (state, action) => {
        extractionAdapter.removeOne(state, action.meta.arg);
        state.isLoading = false;
      });
  },
});

export default extractionSlice.reducer;
